import styles from './index.module.css'
import { IS_ARTIST } from '@disco/constants/FeatureTypes'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import freshSpins from 'assets/images/fresh-spins.png'
import oneWorld from 'assets/images/one-world.png'

import AspectRatio from 'components/ui/aspect-ratio'
import Button from 'components/ui/button'
import Stack from 'components/ui/stack'
import { PartnerDiscounts } from './partner-discounts'
import mixpanel from '@disco/api/mixpanel'

const OpportunityImage = ({
  image,
  fillColor,
  fit = 'cover',
  ratio = 1,
}: {
  image: string
  fillColor?: string
  ratio?: number
  fit?: 'contain' | 'cover' | 'fill' | 'none'
}): React.ReactElement<'div'> => (
  <div className={`${styles.column} ${styles['column--image']}`}>
    <AspectRatio
      image={image}
      fill={fillColor}
      ratio={ratio}
      shape="none"
      fit={fit}
    />
  </div>
)

const OpportunityDescription = ({
  header,
  subheader,
  body,
  cta,
}: {
  header: string
  subheader?: string
  body: React.ReactNode
  cta: React.ReactNode
}): React.ReactElement<'div'> => (
  <div className={styles.column}>
    <div className={styles.row}>
      <h2 className={styles.heading}>{header}</h2>
      {subheader && <p className={styles.subheading}>{subheader}</p>}
    </div>
    <div className={styles.row}>{body}</div>
    <div className={styles.row}>{cta}</div>
  </div>
)

const Opportunity = ({
  containerClassName,
  children,
}: {
  containerClassName: string
  children: React.ReactNode
}): React.ReactElement<'div'> => {
  return (
    <div className={`${styles.container} ${containerClassName}`}>
      {children}
    </div>
  )
}

Opportunity.Image = OpportunityImage
Opportunity.Description = OpportunityDescription

const ArtistOpportunities = (): React.ReactElement<'div'> => {
  const navigate = useNavigate()
  const isArtist: boolean = useFeatureEnabled(IS_ARTIST)

  useEffect(() => {
    if (!isArtist) navigate('/', { replace: true })
  }, [isArtist, navigate])
  return (
    <div className="Page">
      <div className="Page_Content">
        <Stack>
          <Opportunity containerClassName={styles['bg-soul']}>
            <Opportunity.Description
              header="Fresh Spins"
              subheader="Get your music in front of music supervisors"
              body={
                <>
                  <p className={styles.text}>
                    Fresh Spins is DISCO’s quarterly playlist spotlighting the
                    best new music from independent artists. Five tracks are
                    selected each quarter and shared with the music supervisors
                    who use DISCO to find music for film, TV, ads, and more.
                  </p>
                  <p className={styles.text}>
                    Interested in participating? Fill out the form below.
                  </p>
                </>
              }
              cta={
                <Button
                  appearance="primary"
                  caption="Apply Now"
                  link="https://form.typeform.com/to/jKsbFxbC"
                  linkTarget="_blank"
                  onClick={() =>
                    mixpanel.sendPureEvent('Click button', {
                      Location: 'Explorer',
                      Context: 'Opportunities',
                      Campaign: 'Fresh Spins',
                      Copy: 'Apply Now',
                    })
                  }
                />
              }
            />
            <Opportunity.Image image={freshSpins} ratio={570 / 368} />
          </Opportunity>
          <PartnerDiscounts />
        </Stack>
      </div>
    </div>
  )
}

export default ArtistOpportunities
